import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import { CountdownTimer } from '@/components/ui/countdown-timer';
import { Loader2, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

export function ResetPasswordPage() {
  useDocumentTitle('Reset Password');

  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const passwordValidation = useFieldValidation([validators.required(t('errors.passwordRequired')), validators.minLength(6, t('errors.passwordMinLength'))]);
  const confirmValidation = useFieldValidation([
    validators.required(t('errors.confirmPasswordRequired')),
    (v: string) => v !== passwordValidation.value ? t('errors.passwordsDoNotMatch') : null,
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    remaining: number;
    limit: number;
    resetAt: number | undefined;
  } | null>(null);

  const isTokenMissing = !token || token.length < 10;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (passwordValidation.value.length < 6) {
      setError(t('errors.passwordMinLength'));
      return;
    }

    if (passwordValidation.value !== confirmValidation.value) {
      setError(t('errors.passwordsDoNotMatch'));
      return;
    }

    setIsLoading(true);

    try {
      const res = await apiClient.post('/auth/reset-password/confirm', {
        token,
        password: passwordValidation.value,
      });

      if (res.success) {
        setSuccess(true);
        toast({
          title: t('auth.passwordResetSuccess'),
          description: t('auth.passwordResetSuccessDesc'),
          variant: 'success',
        });
      }
    } catch (err: any) {
      const message = err?.message || t('auth.passwordResetFailed');

      if (err.rateLimitRemaining !== undefined) {
        setRateLimitInfo({
          remaining: err.rateLimitRemaining,
          limit: err.rateLimitLimit || 3,
          resetAt: err.rateLimitReset,
        });
      }

      setError(message);
      toast({ title: t('errors.resetFailed'), description: message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  // Invalid or missing token view
  if (isTokenMissing) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-2">
              Dyn<span className="text-dyndev-green">Dev</span>
            </h1>
            <p className="text-[var(--text-secondary)]">{t('app.tagline')}</p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl">{t('auth.invalidResetLink')}</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertCircle size={32} className="text-red-400" />
                </div>
              </div>

              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('auth.invalidResetLinkDesc')}
              </p>

              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium"
              >
                {t('auth.requestNewLink')}
              </Link>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-xs text-[var(--text-secondary)]">
            &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
          </p>
        </div>
      </div>
    );
  }

  // Success view
  if (success) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
        <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />

        <div className="w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-2">
              Dyn<span className="text-dyndev-green">Dev</span>
            </h1>
            <p className="text-[var(--text-secondary)]">{t('app.tagline')}</p>
          </div>

          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl">{t('auth.passwordResetSuccess')}</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 rounded-full bg-dyndev-green/10 flex items-center justify-center">
                  <CheckCircle size={32} className="text-dyndev-green" />
                </div>
              </div>

              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('auth.passwordResetSuccessDesc')}
              </p>

              <Link
                to="/login"
                className="btn-dyndev inline-flex items-center gap-1 text-sm"
              >
                <ArrowLeft size={14} />
                {t('auth.signIn')}
              </Link>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-xs text-[var(--text-secondary)]">
            &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-4">
      {/* Noise overlay */}
      <div className="bg-noise opacity-[0.02] pointer-events-none fixed inset-0" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2">
            Dyn<span className="text-dyndev-green">Dev</span>
          </h1>
          <p className="text-[var(--text-secondary)]">Project Management Portal</p>
        </div>

        {/* Reset Password Card */}
        <Card className="shadow-xl">
          <CardHeader>              <CardTitle className="text-xl">{t('auth.setNewPassword')}</CardTitle>
          </CardHeader>
          <CardContent>              <p className="text-sm text-[var(--text-secondary)] mb-6">
                {t('auth.setNewPasswordDesc')}
              </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Rate limit indicator */}
              {rateLimitInfo && (
                <div className="space-y-2">
                  {rateLimitInfo.remaining > 0 ? (
                    <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs flex items-center gap-2">
                      <div className="flex-1">
                        <span className="font-medium">{rateLimitInfo.remaining}</span>
                        <span className="text-[var(--text-secondary)]"> of </span>
                        <span className="font-medium">{rateLimitInfo.limit}</span>
                        <span className="text-[var(--text-secondary)]"> password reset attempts remaining</span>
                      </div>
                      <div className="w-16 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden flex-shrink-0">
                        <div
                          className="h-full rounded-full bg-amber-400 transition-all duration-300"
                          style={{ width: `${(rateLimitInfo.remaining / rateLimitInfo.limit) * 100}%` }}
                        />
                      </div>
                    </div>                    ) : (
                    <div className="p-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                      <span className="font-medium">Too many password reset attempts.</span>
                      <span className="text-[var(--text-secondary)]">
                        {' '}Try again in{' '}
                        {rateLimitInfo.resetAt ? (
                          <CountdownTimer
                            key={rateLimitInfo.resetAt}
                            resetAt={rateLimitInfo.resetAt}
                            onExpired={() => setRateLimitInfo(null)}
                          />
                        ) : (
                          'a few minutes'
                        )}
                        .
                      </span>
                    </div>
                  )}
                </div>
              )}

              <FormField
                label={t('auth.newPassword')}
                error={passwordValidation.showError ? passwordValidation.error : null}
                showError={passwordValidation.showError}
                isValid={passwordValidation.isValid}
              >
                <Input
                  id="password"
                  type="password"
                  value={passwordValidation.value}
                  onChange={(e) => {
                    passwordValidation.onChange(e);
                    if (rateLimitInfo) setRateLimitInfo(null);
                  }}
                  onBlur={passwordValidation.onBlur}
                  placeholder={t('auth.passwordPlaceholder')}
                />
              </FormField>

              <FormField
                label={t('auth.confirmPassword')}
                error={confirmValidation.showError ? confirmValidation.error : null}
                showError={confirmValidation.showError}
                isValid={confirmValidation.isValid}
              >
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmValidation.value}
                  onChange={(e) => {
                    confirmValidation.onChange(e);
                    if (rateLimitInfo) setRateLimitInfo(null);
                  }}
                  onBlur={confirmValidation.onBlur}
                  placeholder={t('auth.passwordPlaceholder')}
                />
              </FormField>

              <Button
                type="submit"
                disabled={isLoading || !passwordValidation.value.trim() || !confirmValidation.value.trim()}
                size="pill"
                className="w-full"
              >
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isLoading ? t('auth.resetting') : t('auth.resetPassword')}
              </Button>
            </form>

            <div className="text-center mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-dyndev-green hover:text-dyndev-green/80 transition-colors font-medium"
              >
                <ArrowLeft size={14} />
                {t('auth.backToSignIn')}
              </Link>
            </div>
          </CardContent>
        </Card>

        <p className="text-center mt-6 text-xs text-[var(--text-secondary)]">
          &copy; {new Date().getFullYear()} DynDev. {t('app.copyright')}
        </p>
      </div>
    </div>
  );
}
